import { SdkContext } from '../internal'
import type { CommentsToggleResult } from '@/lib/postComments'
import type { HiddenEmbedsResult } from '@/lib/embeds'
import { fetchFile, type DownloadedFile } from '../fileDownload'
import type { PostAckConfirmResult, PostAckList, PostAckReminderPreview, PostAckReminderSent, PostAckStatus } from '@/types/postAck'

export class ContentResource {
  constructor(private ctx: SdkContext) {}

  /** With `limit` (1-100) one page comes back with `pagination.hasNext`; without it, the whole list. `tag` (F-170) filters by one tag. */
  async list(params: { spaceId?: string; limit?: number; page?: number; tag?: string } = {}) {
    return this.ctx.call({ httpMethod: 'GET', path: '/content' }, { query: params })
  }

  async get(id: string) {
    return this.ctx.call({ httpMethod: 'GET', path: `/content/{id}` }, { id })
  }

  async create(content: Record<string, unknown>) {
    return this.ctx.call({ httpMethod: 'POST', path: '/content', hasBody: true }, { body: content })
  }

  async update(id: string, content: Record<string, unknown>) {
    return this.ctx.call({ httpMethod: 'PUT', path: `/content/{id}`, hasBody: true }, { id, body: content })
  }

  /** F-163: turns the post's comments off or on. Sent alone, it changes nothing else (a moderator may do it on any post). */
  /** Only the removed link previews: a light write that answers the post's new `embeds`. */
  async setHiddenEmbeds(id: string, hiddenEmbedUrls: string[]): Promise<HiddenEmbedsResult> {
    return this.ctx.call<HiddenEmbedsResult>(
      { httpMethod: 'PUT', path: `/content/{id}`, hasBody: true },
      { id, body: { hiddenEmbedUrls } }
    )
  }

  async setCommentsDisabled(id: string, commentsDisabled: boolean): Promise<CommentsToggleResult> {
    return this.ctx.call<CommentsToggleResult>(
      { httpMethod: 'PUT', path: `/content/{id}`, hasBody: true },
      { id, body: { commentsDisabled } }
    )
  }

  async delete(id: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/content/{id}` }, { id })
  }

  /** F-248: "Li e estou ciente" on the post's current version (idempotent). 409 `postAck.notRequired` / `postAck.notLive`. */
  async acknowledge(id: string): Promise<PostAckConfirmResult> {
    return this.ctx.call<PostAckConfirmResult>({ httpMethod: 'POST', path: `/content/{id}/acknowledgement`, hasBody: true }, { id, body: {} })
  }

  /** F-248: who confirmed and who did not (the team; 403 for a member). The whole envelope, for `pagination`. */
  async acknowledgements(id: string, query: { status?: PostAckStatus; page?: number; limit?: number } = {}): Promise<PostAckList> {
    const body = await this.ctx.envelope<{ data: Omit<PostAckList, 'pagination'>; pagination?: PostAckList['pagination'] }>(
      { httpMethod: 'GET', path: `/content/{id}/acknowledgements` },
      { id, query }
    )
    return { ...body.data, pagination: body.pagination }
  }

  /** F-248: the CSV (name, e-mail, confirmed at in the community's time zone). */
  async exportAcknowledgements(id: string): Promise<DownloadedFile> {
    return fetchFile(this.ctx, `/content/${encodeURIComponent(id)}/acknowledgements/export`, {}, 'text/csv', `confirmacoes-${id}.csv`)
  }

  /** F-248: "Lembrar quem não confirmou". `preview` only counts; sending is 409 `reminderTooSoon` (`data.nextReminderAt`) or `nobodyPending`. */
  async remindAcknowledgements(id: string, preview: true): Promise<PostAckReminderPreview>
  async remindAcknowledgements(id: string, preview: false): Promise<PostAckReminderSent>
  async remindAcknowledgements(id: string, preview: boolean): Promise<PostAckReminderPreview | PostAckReminderSent> {
    return this.ctx.call<PostAckReminderPreview | PostAckReminderSent>(
      { httpMethod: 'POST', path: `/content/{id}/acknowledgements/remind`, hasBody: true },
      { id, body: { preview } }
    )
  }

  async react(id: string, reactionType: string) {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/content/{id}/react`, hasBody: true },
      { id, body: { type: reactionType } }
    )
  }

  async unreact(id: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/content/{id}/react` }, { id })
  }

  /** `videoMediaId` (F-107): a Drop confirmed with the purpose `contentCommentVideo`; the text may then be empty. */
  async comment(id: string, comment: string, videoMediaId?: string | null) {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/content/{id}/comment`, hasBody: true },
      { id, body: { comment, ...(videoMediaId ? { videoMediaId } : {}) } }
    )
  }

  async getComments(id: string, params: { page?: number; limit?: number } = {}) {
    return this.ctx.call({ httpMethod: 'GET', path: `/content/{id}/comments` }, { id, query: params })
  }

  async reactToComment(contentId: string, commentId: string, reactionType: string) {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/content/{id}/comment/{commentId}/react`, hasBody: true },
      { id: contentId, commentId, body: { type: reactionType } }
    )
  }

  async unreactToComment(contentId: string, commentId: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/content/{id}/comment/{commentId}/react` }, { id: contentId, commentId })
  }

  /** The API reads the text from `comment` (as on create). `videoMediaId`: another id swaps the Drop, `null` removes it. */
  async editComment(contentId: string, commentId: string, comment: string, videoMediaId?: string | null) {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/content/{id}/comment/{commentId}`, hasBody: true },
      { id: contentId, commentId, body: { comment, ...(videoMediaId !== undefined ? { videoMediaId } : {}) } }
    )
  }

  async deleteComment(contentId: string, commentId: string) {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: `/content/{id}/comment/{commentId}` },
      { id: contentId, commentId }
    )
  }
}
